import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Sparkles, ArrowRight } from "lucide-react";
import { welcomeModalContent, WelcomeStep } from "@/data/welcomeModalContent";
import { SubscriptionTier } from "@/lib/subscription";

interface WelcomeModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tier: SubscriptionTier;
  tierName: string;
  onCreateProject: () => void;
}

export const WelcomeModal = ({
  open,
  onOpenChange,
  tier,
  tierName,
  onCreateProject,
}: WelcomeModalProps) => {
  const isPaid = tier !== "free";
  const content = isPaid ? welcomeModalContent.paid : welcomeModalContent.free;

  const handleCtaClick = () => {
    onCreateProject();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader className="text-center sm:text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
            <Sparkles className="h-6 w-6 text-primary" />
          </div>
          <DialogTitle className="text-xl sm:text-2xl font-bold">
            {content.title}
          </DialogTitle>
          <DialogDescription className="text-base text-muted-foreground">
            {content.subtitle}
          </DialogDescription>
        </DialogHeader>

        <div className="mt-2">
          <p className="text-sm text-muted-foreground text-center mb-6">
            {content.description}
          </p>

          {/* Steps */}
          <div className="space-y-4 mb-6">
            {content.steps.map((step: WelcomeStep) => (
              <div key={step.number} className="flex items-start gap-3">
                <div className="flex-shrink-0 flex h-7 w-7 items-center justify-center rounded-full bg-primary text-primary-foreground text-sm font-medium">
                  {step.number}
                </div>
                <div>
                  <h4 className="font-medium text-foreground text-sm">
                    {step.title}
                  </h4>
                  <p className="text-xs text-muted-foreground">
                    {step.description}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* CTA Button */}
          <Button
            onClick={handleCtaClick}
            className="w-full"
            size="lg"
          >
            {content.ctaText}
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>

          {/* Footer Note */}
          {(content.footerNote || isPaid) && (
            <p className="mt-4 text-xs text-muted-foreground text-center">
              {isPaid
                ? `You're on the ${tierName} plan. Enjoy all your premium features!`
                : content.footerNote}
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
